/**
 * Hook: useEnsureConvexUser
 *
 * Automatically creates user in Convex database after Clerk sign-in.
 *
 * LOCAL DEVELOPMENT WORKAROUND:
 * - In production: Users created via Clerk webhooks automatically
 * - In local dev: Webhooks can't reach localhost, so we auto-create users on sign-in
 *
 * Usage:
 *   Add to your App component or root layout:
 *   useEnsureConvexUser();
 *
 * What it does:
 *   1. Waits for Clerk auth to load
 *   2. If user is signed in, calls ensureUserExists mutation
 *   3. Creates user in Convex if doesn't exist
 *   4. Silently fails if already exists (idempotent)
 *
 * This hook can be safely removed when webhooks work in deployed environment.
 */

import { useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { debug, error as logError } from '@/lib/logger';

export function useEnsureConvexUser() {
  const { isSignedIn, isLoaded } = useAuth();
  const ensureUser = useMutation(api.users.ensureUserExists);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) return;

    // Auto-create user in Convex if doesn't exist
    ensureUser()
      .then(result => {
        debug(result.created ? 'User created in Convex' : 'User already exists in Convex', { userId: result.userId });
      })
      .catch(err => {
        logError('Failed to ensure user exists:', err instanceof Error ? err : new Error(String(err)));
      });
  }, [isLoaded, isSignedIn, ensureUser]);
}
